import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { 
  Heart, 
  UserCheck,
  LayoutDashboard, 
  Users, 
  FolderOpen, 
  FileText, 
  ClipboardList,
  FileStack,
  Settings,
  LogOut,
  Sun,
  Moon,
  ChevronRight,
  Search,
  Bell,
  Menu,
  X,
  ScrollText,
  BarChart3,
  Briefcase,
  Settings2,
  Shield,
  Phone,
  CalendarDays,
  UtensilsCrossed,
  FileDown,
  FileSpreadsheet,
  MessageSquare,
  DollarSign,
  ClipboardCheck,
  Globe,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import GlobalMessenger from './GlobalMessenger';
import AppointmentStatusAlert from './AppointmentStatusAlert';
import TaskDueAlert from './TaskDueAlert';
import { FlagAlertWatcher } from './caseload-flags/FlagAlertWatcher';
import { BookedForYouNotice } from './BookedForYouNotice';
import { HelpConnectMessageAlert } from './HelpConnectMessageAlert';
import { SignInAsDialog, SignedInAsBanner, SIGN_IN_AS_ROLES } from './SignInAs';

// Navigation items with permission requirements
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'view_dashboard' },
  { name: 'Participants', href: '/participants', icon: Users, permission: 'view_participants' },
  { name: 'Appointments', href: '/appointments', icon: CalendarDays, permission: 'view_dashboard' },
  { name: 'Food Pantry Queue', href: '/food-pantry/queue', icon: UtensilsCrossed, permission: 'view_dashboard' },
  { name: 'Referral Tracker', href: '/referral-tracker', icon: ClipboardCheck, roles: ['system_admin', 'executive', 'executive_leadership', 'operations_manager', 'case_manager_supervisor', 'case_manager'] },
  { name: 'Communications', href: '/communications', icon: MessageSquare, permission: 'view_communications' },
  { name: 'Mass Case Notes', href: '/mass-case-notes', icon: FileSpreadsheet, permission: 'create_case_notes' },
  { name: 'Caseload Review', href: '/caseload-review', icon: ClipboardCheck, permission: 'view_reports' },
  { name: 'Reports', href: '/reports', icon: BarChart3, permission: 'view_reports' },
  { name: 'Forms', href: '/forms', icon: ClipboardList, permission: 'view_dashboard' },
];

// Admin navigation with permission requirements
const adminNavigation = [
  { name: 'Contracts', href: '/admin/contracts', icon: ScrollText, permission: 'view_contracts' },
  { name: 'User Management', href: '/users', icon: Users, permission: 'manage_users' },
  { name: 'Roles & Permissions', href: '/admin/roles', icon: Shield, permission: 'manage_roles' },
  { name: 'Participant Costs', href: '/admin/participant-costs', icon: DollarSign, permission: 'view_admin' },
  { name: 'PDF Export Settings', href: '/admin/pdf-settings', icon: FileDown, permission: 'manage_system_settings' },
  { name: 'Settings', href: '/settings', icon: Settings, permission: 'view_admin' },
  { name: 'Audit Logs', href: '/admin/audit-logs', icon: Shield, permission: 'view_audit_logs' },
  { name: 'HELPConnect Resources', href: '/admin/helpconnect-resources', icon: FolderOpen, systemAdminOnly: true },
];

const AppLayout = ({ children }) => {
  const { user, logout, hasPermission, getRoleDisplayName, isAdmin: checkIsAdmin } = useAuth();
  const [showSignInAs, setShowSignInAs] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => localStorage.getItem('myhelp_sidebar_collapsed') === '1');
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem('myhelp_sidebar_collapsed', c ? '0' : '1'); return !c; });
  // Collapsed strip applies on desktop widths only (lg); phones keep the slide-out menu
  const hideLabel = collapsed ? 'lg:hidden' : '';
  const [globalSearch, setGlobalSearch] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGlobalSearch = (e) => {
    if (e.key === 'Enter' && globalSearch.trim()) {
      navigate(`/participants?search=${encodeURIComponent(globalSearch.trim())}`);
      setGlobalSearch('');
    }
  };

  // Check if user is admin based on permissions
  const isAdmin = checkIsAdmin();

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter(item => (item.roles ? item.roles.includes(user?.role) : hasPermission(item.permission)));
  const filteredAdminNavigation = adminNavigation.filter(item => (item.systemAdminOnly ? user?.role === 'system_admin' : hasPermission(item.permission)));

  const IconTip = ({ label, children }) => (collapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" className="hidden lg:block">{label}</TooltipContent>
    </Tooltip>
  ) : children);

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isSubItemActive = hasSubItems && item.subItems.some(sub => 
      location.pathname === sub.href || location.pathname.startsWith(sub.href + '/')
    );
    const [expanded, setExpanded] = useState(isActive || isSubItemActive);
    
    if (hasSubItems) {
      const openGroup = () => {
        if (collapsed && window.matchMedia('(min-width: 1024px)').matches) {
          const first = item.subItems.find(sub => hasPermission(sub.permission));
          if (first) navigate(item.href || first.href);
          return;
        }
        setExpanded(!expanded);
      };
      return (
        <div>
          <IconTip label={item.name}>
          <button
            onClick={openGroup}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 w-full",
              collapsed && "lg:justify-center lg:px-0",
              (isActive || isSubItemActive)
                ? "bg-primary/10 text-primary" 
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
            aria-label={item.name}
            data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className="w-5 h-5 shrink-0" />
            <span className={hideLabel}>{item.name}</span>
            <ChevronRight className={cn("w-4 h-4 ml-auto transition-transform", expanded && "rotate-90", hideLabel)} />
          </button>
          </IconTip>
          {expanded && (
            <div className={cn("ml-4 mt-1 space-y-1", hideLabel)}>
              {item.subItems.filter(sub => hasPermission(sub.permission)).map((subItem) => {
                const isSubActive = location.pathname === subItem.href || location.pathname.startsWith(subItem.href + '/');
                return (
                  <Link
                    key={subItem.name}
                    to={subItem.href}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                      isSubActive 
                        ? "bg-primary text-primary-foreground shadow-sm" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {subItem.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <IconTip label={item.name}>
      <Link
        to={item.href}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
          collapsed && "lg:justify-center lg:px-0",
          isActive 
            ? "bg-primary text-primary-foreground shadow-sm" 
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        )}
        onClick={() => setSidebarOpen(false)}
        aria-label={item.name}
        data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
      >
        <item.icon className="w-5 h-5 shrink-0" />
        <span className={hideLabel}>{item.name}</span>
        {isActive && <ChevronRight className={cn("w-4 h-4 ml-auto", hideLabel)} />}
      </Link>
      </IconTip>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-[transform,width] duration-200 ease-in-out lg:translate-x-0",
        collapsed && "lg:w-16",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )} data-testid="app-sidebar" data-collapsed={collapsed ? 'true' : 'false'}>
        <TooltipProvider delayDuration={100}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={cn("flex items-center justify-between h-16 px-4 border-b border-border", collapsed && "lg:justify-center lg:px-0")}>
            <Link to="/dashboard" className={cn("flex items-center gap-2", hideLabel)} data-testid="logo-link">
              <img 
                src="/myhelp-logo.png" 
                alt="MyHELP" 
                className="h-10 w-auto dark:bg-white dark:rounded-md dark:px-1.5 dark:py-0.5"
              />
            </Link>
            <IconTip label="Expand menu">
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:inline-flex shrink-0"
                onClick={toggleCollapsed}
                aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                title={collapsed ? undefined : 'Collapse menu'}
                data-testid="sidebar-collapse-toggle"
              >
                {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
              </Button>
            </IconTip>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className={cn("px-3 space-y-1", collapsed && "lg:px-2")}>
              <p className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider", hideLabel)}>
                Main Menu
              </p>
              {filteredNavigation.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}

              {filteredAdminNavigation.length > 0 && (
                <>
                  <div className={cn("pt-4", collapsed && "lg:pt-2 lg:mt-2 lg:border-t lg:border-border")}>
                    <p className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider", hideLabel)}>
                      Administration
                    </p>
                  </div>
                  {filteredAdminNavigation.map((item) => (
                    <NavLink key={item.name} item={item} />
                  ))}
                </>
              )}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className={cn("p-4 border-t border-border", collapsed && "lg:p-2")}>
            <div className={cn("flex items-center gap-3 px-2 py-2", collapsed && "lg:justify-center lg:px-0")}>
              <Avatar className="h-9 w-9" title={collapsed ? user?.name : undefined}>
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                  {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className={cn("flex-1 min-w-0", hideLabel)}>
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {getRoleDisplayName(user?.role)}
                </p>
              </div>
            </div>
          </div>
        </div>
        </TooltipProvider>
      </aside>

      {/* Main content */}
      <div className={cn("transition-[padding] duration-200", collapsed ? "lg:pl-16" : "lg:pl-64")}>
        {/* Top header */}
        <SignedInAsBanner />
        <header className="sticky top-0 z-30 glass-header">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              
              {/* Search */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-muted rounded-lg w-64">
                <Search className="w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search participants..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  data-testid="global-search-input"
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  onKeyDown={handleGlobalSearch}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </Button>

              {/* Notifications */}
              <Button variant="ghost" size="icon" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
              </Button>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" data-testid="profile-menu-item">
                      <Settings className="w-4 h-4 mr-2" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {SIGN_IN_AS_ROLES.includes(user?.role) && !user?.impersonator_id && (
                    <DropdownMenuItem onClick={() => setShowSignInAs(true)} data-testid="sign-in-as-menu-item">
                      <UserCheck className="w-4 h-4 mr-2" />
                      Sign in as
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <SignInAsDialog open={showSignInAs} onOpenChange={setShowSignInAs} />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>

      {/* Global SMS Messenger - persists across all pages */}
      <GlobalMessenger />
      
      {/* Appointment Status Alert - blocking popup for overdue status updates */}
      <AppointmentStatusAlert />
      
      {/* Task Due Alert - shows once per login session for due/overdue tasks */}
      <TaskDueAlert />
      <FlagAlertWatcher />
      <BookedForYouNotice />
      <HelpConnectMessageAlert />
    </div>
  );
};

export default AppLayout;
